import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api';
import {
  FileText, RefreshCw, Loader2, AlertTriangle, PenTool, Save, Send,
  Download, Upload, Eye, CheckCircle2, Building2, Archive, IndianRupee,
} from 'lucide-react';

// DCS ATS — Agreement Generator.
// The page UI is unchanged (Generate / Pipeline). What changed: the live preview
// AND the PDF are produced by JM's EXACT engine (its template, fonts, spacing,
// colour and the signature/stamp in its exact spot), run inside a hidden
// /agreement-generator.html iframe and driven by this form — so the agreement is
// byte-identical to JM. Save stores that exact PDF against the client (and writes
// the billing columns); Pipeline downloads the stored PDF with auth.

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const OPS_DEFAULTS = [
  ['2.10', 'Operational Recruitment Cost'],
  ['2.11', 'Fair Usage of Recruitment Services'],
  ['2.12', 'Candidate Processing Charges'],
  ['2.13', 'Mandatory Interview Feedback'],
  ['2.14', 'Dormant Recruitment Mandates'],
  ['2.15', 'Candidate Ownership'],
  ['2.16', 'Volume Hiring Commitment'],
  ['2.17', 'Excessive Candidate Requests'],
  ['2.18', ''], ['2.19', ''], ['2.20', ''], ['2.21', ''], ['2.22', ''],
];

const BLANK = {
  agreementDate: todayISO(), companyName: '', cin: '', companyAddress: '',
  invoiceAddress: '', invoiceDeliveryAddress: '', contactPerson: '', gstNumber: '',
  panNumber: '', state: '', feeType: 'percentage', feeValue: '50', taxMode: 'gst',
  paymentDays: '7', candidateReplacementDays: '45', companyTerminationDays: '45',
  signatoryName: '', signatoryDesignation: '',
  sameInvoice: true, sameDelivery: true, includeRateCard: true, signAgreement: false,
  includeCustomTerms: false, customTermsText: '',
};

const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600', generated: 'bg-indigo-100 text-indigo-700',
  sent: 'bg-blue-100 text-blue-700', opened: 'bg-emerald-100 text-emerald-800',
  active: 'bg-green-100 text-green-800', archived: 'bg-slate-200 text-slate-500',
};

function b64Download(b64, filename) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const Field = ({ label, id, form, set, type = 'text', textarea, disabled, required, placeholder, list }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1">
      {label}{required ? <span className="text-red-500"> *</span> : null}
    </label>
    {textarea ? (
      <textarea rows={3} value={form[id] || ''} disabled={disabled} placeholder={placeholder}
        onChange={(e) => set(id, e.target.value)}
        className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none
                   focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400" />
    ) : (
      <input type={type} value={form[id] || ''} disabled={disabled} placeholder={placeholder}
        list={list} onChange={(e) => set(id, e.target.value)}
        className="w-full border border-slate-200 rounded-lg px-2 min-h-[40px] text-sm outline-none
                   focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400" />
    )}
  </div>
);

const Toggle = ({ label, id, form, set, hint }) => (
  <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
    <input type="checkbox" checked={!!form[id]} onChange={(e) => set(id, e.target.checked)}
      className="mt-0.5 w-4 h-4" />
    <span>{label}{hint ? <span className="block text-xs text-slate-400">{hint}</span> : null}</span>
  </label>
);

export default function AgreementGeneratorPage() {
  const [tab, setTab] = useState('generate');
  const [form, setForm] = useState({ ...BLANK });
  const [ops, setOps] = useState(OPS_DEFAULTS.map(([num, title]) => ({ num, title, body: '' })));
  const [opsSel, setOpsSel] = useState(OPS_DEFAULTS.map(() => false));
  const [clients, setClients] = useState([]);
  const [previewed, setPreviewed] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [agreements, setAgreements] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [engineReady, setEngineReady] = useState(false);
  const iframeRef = useRef(null);

  // ---- talk to the hidden JM engine iframe ----
  useEffect(() => {
    const onReady = (ev) => { if ((ev.data || {}).type === 'dcs-ready') setEngineReady(true); };
    window.addEventListener('message', onReady);
    return () => window.removeEventListener('message', onReady);
  }, []);

  const askEngine = useCallback((type, data) => new Promise((resolve, reject) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return reject(new Error('Generator is still loading — try again in a second.'));
    const reqId = `${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const okType = type === 'dcs-render' ? 'dcs-render-result' : 'dcs-save-result';
    const errType = type === 'dcs-render' ? 'dcs-render-error' : 'dcs-save-error';
    let done = false;
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.reqId !== reqId) return;
      done = true; window.removeEventListener('message', onMsg);
      if (d.type === okType) resolve(d); else reject(new Error(d.error || 'Failed.'));
    };
    window.addEventListener('message', onMsg);
    w.postMessage({ type, reqId, ...data }, '*');
    setTimeout(() => { if (!done) { window.removeEventListener('message', onMsg); reject(new Error('Timed out generating the agreement.')); } }, 30000);
  }), []);

  const set = (id, v) => {
    setForm((f) => {
      const next = { ...f, [id]: v };
      if (id === 'companyAddress' && next.sameInvoice) next.invoiceAddress = v;
      if ((id === 'invoiceAddress' || (id === 'companyAddress' && next.sameInvoice)) && next.sameDelivery)
        next.invoiceDeliveryAddress = next.invoiceAddress;
      if (id === 'sameInvoice' && v) next.invoiceAddress = next.companyAddress;
      if (id === 'sameDelivery' && v) next.invoiceDeliveryAddress = next.invoiceAddress;
      return next;
    });
    setPreviewed(false);
  };

  const loadClients = useCallback(async () => {
    try { const r = await api.get('/agreements/clients'); setClients(r.data.clients || []); }
    catch (e) { /* non-fatal */ }
  }, []);

  const loadAgreements = useCallback(async () => {
    try {
      const r = await api.get('/agreements');
      setAgreements(r.data.agreements || []); setBuckets(r.data.buckets || {});
    } catch (e) { setError(e?.response?.data?.detail || 'Could not load agreements.'); }
  }, []);

  useEffect(() => { loadClients(); loadAgreements(); }, [loadClients, loadAgreements]);

  const applyClient = (c) => {
    const d = c.agreement_defaults || {};
    setForm((f) => ({
      ...f, companyName: c.name || f.companyName, contactPerson: c.contact_person || '',
      companyAddress: c.address || '', gstNumber: c.gstin || '', panNumber: c.pan || '',
      state: c.state || '', cin: d.cin || f.cin,
      invoiceAddress: d.invoiceAddress || '', invoiceDeliveryAddress: d.invoiceDeliveryAddress || '',
      sameInvoice: !d.invoiceAddress, sameDelivery: !d.invoiceDeliveryAddress,
      feeType: d.feeType || 'percentage', feeValue: d.feeValue || '50', taxMode: d.taxMode || 'gst',
      paymentDays: d.paymentDays || '7', candidateReplacementDays: d.candidateReplacementDays || '45',
      companyTerminationDays: d.companyTerminationDays || '45',
      signatoryName: d.signatoryName || '', signatoryDesignation: d.signatoryDesignation || '',
    }));
  };
  useEffect(() => {
    const hit = clients.find((c) => (c.name || '').toLowerCase() === (form.companyName || '').toLowerCase());
    if (hit) applyClient(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.companyName]);

  const payload = useMemo(() => ({
    ...form, opsSelected: opsSel, opsClauses: ops,
    clientId: (clients.find((c) => (c.name || '').toLowerCase() === (form.companyName || '').toLowerCase()) || {}).id || null,
    agreementId: editingId,
  }), [form, ops, opsSel, clients, editingId]);

  const engineValues = (sign) => ({
    ...form, signAgreement: !!sign || !!form.signAgreement, opsSelected: opsSel, opsClauses: ops,
  });

  const doPreview = async (sign) => {
    setError(''); setOk(''); setBusy('preview');
    if (sign) set('signAgreement', true);
    try {
      await askEngine('dcs-render', { values: engineValues(sign), sign: !!sign });
      setPreviewed(true);
    } catch (e) { setError(e.message || 'Preview failed.'); }
    finally { setBusy(''); }
  };

  const downloadCurrent = async () => {
    setError(''); setOk(''); setBusy('download');
    try {
      const res = await askEngine('dcs-save-request', { values: engineValues(form.signAgreement) });
      const company = (form.companyName || 'client').replace(/[^a-z0-9-_ ]/gi, '').slice(0, 50) || 'client';
      b64Download(res.pdf_base64, res.filename || `dcs-agreement-${company}.pdf`);
    } catch (e) { setError(e.message || 'Download failed.'); }
    finally { setBusy(''); }
  };

  const doSave = async (saveAs) => {
    setError(''); setOk(''); setBusy('save');
    try {
      const eng = await askEngine('dcs-save-request', { values: engineValues(form.signAgreement) });
      const r = await api.post('/agreements/save', { ...payload, saveAs, pdf_base64: eng.pdf_base64 });
      setOk(`Saved. Customer ${r.data.clientCreated ? 'created' : 'updated'} · status ${r.data.status}.`);
      setEditingId(r.data.agreementId);
      await loadAgreements(); await loadClients();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? `Missing: ${(d.fields || []).join(', ')}` : (d || e.message || 'Save failed.'));
    } finally { setBusy(''); }
  };

  const resetForm = () => {
    setForm({ ...BLANK, agreementDate: todayISO() });
    setOps(OPS_DEFAULTS.map(([num, title]) => ({ num, title, body: '' })));
    setOpsSel(OPS_DEFAULTS.map(() => false));
    setPreviewed(false); setEditingId(null); setOk(''); setError('');
  };

  // ---- pipeline actions ----
  const download = async (id, signed) => {
    setError('');
    try {
      const r = await api.get(`/agreements/${id}/pdf${signed ? '?signed=true' : ''}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `dcs-agreement-${id.slice(0, 8)}${signed ? '-signed' : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'No PDF stored for this one — open it in Generate and Save again.' : 'Download failed.');
    }
  };

  const send = async (id) => {
    const to = window.prompt('Send the agreement PDF to (email):');
    if (!to) return;
    setBusy(`send-${id}`); setError('');
    try { await api.post(`/agreements/${id}/send`, { recipients: [to] }); setOk('Sent.'); await loadAgreements(); }
    catch (e) {
      const d = e?.response?.data?.detail || '';
      setError(String(d).includes('535') || String(d).toLowerCase().includes('credential')
        ? 'Gmail rejected the login — set a Gmail App Password in Settings → email, then retry.'
        : (d || 'Send failed.'));
    } finally { setBusy(''); }
  };

  const uploadSigned = async (id, file) => {
    setBusy(`sign-${id}`); setError('');
    try {
      const b64 = await new Promise((res, rej) => {
        const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file);
      });
      const r = await api.post(`/agreements/${id}/upload-countersigned`, { signed_pdf_base64: b64 });
      setOk(`Countersigned. Customer ${r.data.customerActivated ? 'ACTIVATED' : 'unchanged'}, chase completed.`);
      await loadAgreements(); await loadClients();
    } catch (e) { setError(e?.response?.data?.detail || 'Upload failed.'); }
    finally { setBusy(''); }
  };

  const archive = async (id) => {
    if (!window.confirm('Archive this agreement?')) return;
    try { await api.post(`/agreements/${id}/archive`, { archived: true }); await loadAgreements(); }
    catch (e) { setError(e?.response?.data?.detail || 'Archive failed.'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5" data-testid="agreement-generator">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6" /> Agreement Generator
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Recruitment services agreement · fee stated on <b>annual CTC</b> · exact DCS layout &amp; signature.
          </p>
        </div>
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5 bg-white">
          {[['generate', 'Generate'], ['pipeline', `Pipeline (${agreements.length})`]].map(([id, l]) => (
            <button key={id} onClick={() => { setTab(id); if (id === 'pipeline') loadAgreements(); }}
              className={`px-3 min-h-[38px] text-sm font-medium rounded-md ${tab === id ? 'bg-slate-900 text-white' : 'text-slate-600'}`}>{l}</button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      ) : null}
      {ok ? (
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2 text-sm">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> <span>{ok}</span>
        </div>
      ) : null}

      {tab === 'generate' ? (
        <div className="grid lg:grid-cols-2 gap-5">
          {/* ---------------- form ---------------- */}
          <div className="space-y-4">
            {editingId ? (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-1.5">
                Editing saved agreement {editingId.slice(0, 8)}… · <button className="underline" onClick={resetForm}>start new</button>
              </div>
            ) : null}

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Company
              </p>
              <Field label="Agreement date" id="agreementDate" type="date" form={form} set={set} required />
              <Field label="Company legal name" id="companyName" form={form} set={set} required
                     list="clientNames" placeholder="Start typing — known clients auto-fill" />
              <datalist id="clientNames">
                {clients.map((c) => <option key={c.id} value={c.name} />)}
              </datalist>
              <Field label="CIN / LLPIN" id="cin" form={form} set={set} required />
              <Field label="Company address" id="companyAddress" form={form} set={set} textarea required />
              <Toggle label="Invoice address same as company address" id="sameInvoice" form={form} set={set} />
              <Field label="Invoice address" id="invoiceAddress" form={form} set={set} textarea
                     disabled={form.sameInvoice} required />
              <Toggle label="Invoice delivery address same as invoice address" id="sameDelivery" form={form} set={set} />
              <Field label="Invoice delivery address" id="invoiceDeliveryAddress" form={form} set={set} textarea
                     disabled={form.sameDelivery} required />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact person" id="contactPerson" form={form} set={set} required />
                <Field label="State" id="state" form={form} set={set} required />
                <Field label="GST number" id="gstNumber" form={form} set={set} required />
                <Field label="PAN number" id="panNumber" form={form} set={set} required />
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <IndianRupee className="w-3.5 h-3.5" /> Commercial terms
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Fee type</label>
                  <select value={form.feeType} onChange={(e) => set('feeType', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-2 min-h-[40px] text-sm">
                    <option value="percentage">Percentage of annual CTC</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <Field label={form.feeType === 'fixed' ? 'Fee value (₹)' : 'Fee value (% of annual CTC)'}
                       id="feeValue" form={form} set={set} required />
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">GST mode</label>
                  <select value={form.taxMode} onChange={(e) => set('taxMode', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-2 min-h-[40px] text-sm">
                    <option value="gst">+18% GST</option>
                    <option value="non-gst">NON GST</option>
                  </select>
                </div>
                <Field label="Payment collection days" id="paymentDays" type="number" form={form} set={set} required />
                <Field label="Candidate-leaves replacement days" id="candidateReplacementDays" type="number" form={form} set={set} required />
                <Field label="Company termination days" id="companyTerminationDays" type="number" form={form} set={set} required />
              </div>
              <p className="text-[11px] text-slate-400">
                The billing trigger reads these: on joining + payment-days, admin and super-admin are
                notified with the fee = {form.feeType === 'fixed' ? 'the fixed amount' : `${form.feeValue || '—'}% of the candidate's annual CTC`}.
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signatory</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Signatory name" id="signatoryName" form={form} set={set} required />
                <Field label="Signatory designation" id="signatoryDesignation" form={form} set={set} required />
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Options</p>
              <Toggle label="Append Service Fee Rate Card" id="includeRateCard" form={form} set={set} />
              <Toggle label="Sign agreement (apply DCS signature & stamp)" id="signAgreement" form={form} set={set}
                      hint="Burns the DCS signature & stamp onto the signature page" />
              <Toggle label="Append custom terms" id="includeCustomTerms" form={form} set={set} />
              {form.includeCustomTerms ? (
                <Field label="Custom terms text" id="customTermsText" form={form} set={set} textarea />
              ) : null}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center justify-between">
                Operational Recruitment Cost clauses
                <span className="flex gap-2">
                  <button className="text-slate-500 underline"
                          onClick={() => setOpsSel(ops.map((c) => !!(c.title || '').trim() || !!(c.body || '').trim()))}>Select all</button>
                  <button className="text-slate-500 underline" onClick={() => setOpsSel(OPS_DEFAULTS.map(() => false))}>Clear</button>
                </span>
              </p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {ops.map((c, i) => {
                  const extra = i >= 8;
                  return (
                    <div key={c.num} className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={opsSel[i]}
                        disabled={extra && !(c.title || '').trim()}
                        onChange={(e) => setOpsSel((s) => s.map((v, j) => (j === i ? e.target.checked : v)))}
                        className="mt-1 w-4 h-4" />
                      <div className="flex-1 min-w-0">
                        {extra ? (
                          <input value={c.title} placeholder={`${c.num} — custom clause title`}
                            onChange={(e) => setOps((o) => o.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                            className="w-full border border-slate-200 rounded px-2 py-1 text-xs" />
                        ) : (
                          <span className="text-slate-700"><b>{c.num}</b> {c.title}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 sticky bottom-0 bg-white/90 backdrop-blur py-2">
              <button onClick={() => doPreview(false)} disabled={busy === 'preview'}
                className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm font-medium border border-slate-200 text-slate-700">
                {busy === 'preview' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
              </button>
              <button onClick={() => doPreview(true)} disabled={busy === 'preview'}
                className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm font-medium border border-slate-200 text-slate-700">
                <PenTool className="w-4 h-4" /> Sign &amp; Preview
              </button>
              <button onClick={downloadCurrent} disabled={busy === 'download'}
                className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm font-medium border border-slate-200 text-slate-700">
                {busy === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
              </button>
              <button onClick={() => doSave('client')} disabled={busy === 'save'}
                className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm font-semibold bg-slate-900 text-white">
                {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Update' : 'Save & create customer'}
              </button>
              <button onClick={() => doSave('draft')} disabled={busy === 'save'}
                className="inline-flex items-center gap-1.5 px-3 min-h-[44px] rounded-lg text-sm border border-slate-200 text-slate-600">
                Save as draft
              </button>
              <button onClick={resetForm} className="px-3 min-h-[44px] rounded-lg text-sm text-slate-500">Clear</button>
            </div>
          </div>

          {/* ---------------- preview: this iframe IS JM's engine ----------------
               It must stay VISIBLE and full-size — that is what lets JM's
               html2canvas lay out and capture the pages (a hidden/1x1 iframe
               is exactly what broke Download/Sign before). The engine hides its
               own form in embed mode and shows only the rendered agreement. */}
          <div className="lg:sticky lg:top-4 h-fit">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-500 flex items-center justify-between">
                <span>Preview</span>
                <span className="text-slate-400 font-normal">
                  {!engineReady ? 'loading engine…' : previewed ? 'exact DCS agreement' : 'press Preview'}
                </span>
              </div>
              <iframe ref={iframeRef} src="/agreement-generator.html" title="agreement-engine"
                className="w-full h-[78vh] bg-[#e9edf2]" style={{ border: 0 }} />
            </div>
          </div>
        </div>
      ) : (
        /* ---------------- pipeline ---------------- */
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {Object.entries(buckets).map(([k, v]) => (
              <span key={k} className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_STYLE[k] || 'bg-slate-100'}`}>
                {k}: {v}
              </span>
            ))}
            <button onClick={loadAgreements} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-600">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
          {agreements.length === 0 ? (
            <p className="text-sm text-slate-500 py-6">No agreements yet.</p>
          ) : agreements.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[a.status] || 'bg-slate-100'}`}>{a.status}</span>
                    <span className="font-semibold text-slate-900">{a.client_name || 'Unknown client'}</span>
                    {a.client_active === false ? (
                      <span className="text-[10px] text-red-600 font-medium">client inactive</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {a.fee_basis === 'fixed' ? `Fixed ₹${a.fee_value}` : `${a.fee_value}% of annual CTC`}
                    {' · '}pay in {a.payment_due_days}d · replace {a.replacement_days}d
                    {a.sent_to ? ` · sent to ${a.sent_to}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => download(a.id, false)} title="Download PDF"
                    className="inline-flex items-center gap-1 px-2.5 min-h-[38px] rounded-lg text-xs border border-slate-200 text-slate-700">
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                  <button onClick={() => send(a.id)} disabled={busy === `send-${a.id}`} title="Send"
                    className="inline-flex items-center gap-1 px-2.5 min-h-[38px] rounded-lg text-xs border border-slate-200 text-slate-700">
                    <Send className="w-3.5 h-3.5" /> Send
                  </button>
                  <label className="inline-flex items-center gap-1 px-2.5 min-h-[38px] rounded-lg text-xs border border-slate-200 text-slate-700 cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> Countersigned
                    <input type="file" accept="application/pdf" className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadSigned(a.id, e.target.files[0])} />
                  </label>
                  {a.signed_uploaded_at ? (
                    <button onClick={() => download(a.id, true)}
                      className="inline-flex items-center gap-1 px-2.5 min-h-[38px] rounded-lg text-xs border border-emerald-200 text-emerald-700">
                      <Download className="w-3.5 h-3.5" /> Signed
                    </button>
                  ) : null}
                  <button onClick={() => archive(a.id)} title="Archive"
                    className="inline-flex items-center px-2 min-h-[38px] rounded-lg text-xs text-slate-400">
                    <Archive className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
